import { parse } from '@loaders.gl/core';
import { CSVLoader } from '@loaders.gl/csv';
import { useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { apiPrefix } from '../../config';
import { getAuthHeader } from '../../helpers/auth';
import { getLowercaseKeys } from '../../helpers/object';
import Toasts from '../common/Toasts';

const REQUIRED_COLUMNS = ['developer', 'developmentname', 'region', 'url'];

const VolumeURL = () => {
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [fileSelected, setFileSelected] = useState<File | null>(null);
  const uploadFileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: any) => {
    const file = e.target.files?.[0] ?? null;
    setFileSelected(file);
  };

  const uploadFile = async (file: File) => {
    const csvData = await parse(file, CSVLoader);
    const rows = csvData.map((e: any) => getLowercaseKeys(e));

    const missing = REQUIRED_COLUMNS.filter((c) => !Object.keys(rows[0] ?? {}).includes(c));
    if (missing.length > 0) {
      throw new Error(`CSV is missing required columns: ${missing.join(', ')}`);
    }

    const filtered = rows.filter(
      (row: any) => !REQUIRED_COLUMNS.every((col) => String(row[col]).toLowerCase() === col)
    );

    const requestPath = apiPrefix + '/volume_url_uploads/';
    const response = await fetch(requestPath, {
      method: 'POST',
      headers: await getAuthHeader({ isJson: true }),
      body: JSON.stringify({ urls: filtered }),
    });

    if (response.ok) {
      setInfo('CSV uploaded successfully');
      setFileSelected(null);
      if (uploadFileRef.current) uploadFileRef.current.value = '';
      setTimeout(() => setInfo(null), 5000);
    } else {
      const data = await response.json();
      setError(data.error ?? 'Upload failed');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleUpload = async () => {
    if (!fileSelected) return;
    setUploading(true);
    try {
      await uploadFile(fileSelected);
    } catch (err: any) {
      setError(err?.message ?? 'Upload failed');
      setTimeout(() => setError(null), 5000);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container className="my-4" style={{ position: 'relative' }}>
      <Toasts info={info} error={error} setInfo={setInfo} setError={setError} />
      <Form.Group as={Row} className="mb-3">
        <Form.Label column sm>
          Upload CSV
        </Form.Label>
        <Col sm={4}>
          <Form.Control
            ref={uploadFileRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
          />
        </Col>
        <Col sm={6}>
          <Button
            onClick={handleUpload}
            disabled={fileSelected == null || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Col>
      </Form.Group>
    </Container>
  );
};

export default VolumeURL;
